import styled from 'styled-components';
import { shade, transparentize } from 'polished';
import {
  brand,
  breakpoints,
  ink,
  surface,
  tones,
  touchTarget,
} from '../../../styles/brand';

/** Azul da marca escurecido: fundo do card, separa o telão do resto do card. */
const boardBg = shade(0.6, brand.blue);
const glass = transparentize(0.86, brand.white);

export const Card = styled.section`
  grid-area: board;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  min-width: 0;
  padding: 1rem;
  border-radius: 1rem;
  background: ${boardBg};
  color: ${brand.white};

  @media (max-width: ${breakpoints.lg}) and (min-width: calc(${breakpoints.md} + 0.0625rem)) {
    flex-direction: row;
    align-items: center;
    gap: 1.25rem;
  }
`;

export const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  min-width: 0;
  flex: 1;
`;

export const QrFrame = styled.div`
  flex: none;
  padding: 0.3rem;
  border-radius: 0.6rem;
  background: #fff;
  line-height: 0;
`;

export const IconBox = styled.span`
  flex: none;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  display: grid;
  place-items: center;
  background: ${glass};
  color: ${brand.orange};

  svg {
    width: 1.3rem;
    height: 1.3rem;
  }
`;

export const Texts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;

  h3 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-family: 'RedHatDisplay-Bold';
    font-size: 1rem;
    letter-spacing: -0.01em;

    svg {
      width: 1.1rem;
      height: 1.1rem;
      color: ${brand.orange};
    }
  }

  p {
    margin: 0;
    font-family: 'RedHatDisplay-Regular';
    font-size: 0.8rem;
    line-height: 1.4;
    color: ${transparentize(0.25, brand.white)};
  }
`;

export const Actions = styled.div<{ $compact?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $compact }) =>
    $compact ? `1fr 1fr ${touchTarget}` : '1fr 1fr'};
  gap: 0.5rem;
  flex: none;

  @media (max-width: ${breakpoints.lg}) and (min-width: calc(${breakpoints.md} + 0.0625rem)) {
    min-width: 17rem;
  }
`;

export const ActionButton = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-height: ${touchTarget};
  padding: 0 0.75rem;
  border: 0;
  border-radius: 0.7rem;
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.85rem;
  white-space: nowrap;
  cursor: pointer;
  touch-action: manipulation;
  background: ${({ $primary }) => ($primary ? brand.orange : glass)};
  color: ${brand.white};
  transition:
    transform 100ms ease-out,
    background 160ms ease;

  svg {
    width: 1.05rem;
    height: 1.05rem;
    flex: none;
  }

  &:active {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: 2px solid ${brand.white};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    &:active {
      transform: none;
    }
  }
`;

export const Downloads = styled.div`
  display: flex;
  gap: 1rem;
`;

export const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  min-height: 2rem;
  padding: 0;
  border: 0;
  background: none;
  font-family: 'RedHatDisplay-Medium';
  font-size: 0.8rem;
  color: ${transparentize(0.2, brand.white)};
  cursor: pointer;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    color: ${brand.white};
  }
`;

/* ── conteúdo do bottom sheet (fundo branco) ─────────────────────────────── */

export const SheetQr = styled.div`
  padding: 0.5rem;
  border-radius: 0.9rem;
  border: 1px solid ${surface.line};
  background: #fff;
  line-height: 0;
`;

export const SheetText = styled.p`
  margin: 0;
  max-width: 22rem;
  text-align: center;
  font-family: 'RedHatDisplay-Regular';
  font-size: 0.85rem;
  color: ${tones.gray.text};
`;

export const LinkBox = styled.div`
  width: 100%;
  max-width: 26rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-height: ${touchTarget};
  padding: 0 0.3rem 0 0.75rem;
  border-radius: 0.7rem;
  background: ${surface.page};

  span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.8rem;
    color: ${ink};
  }

  button {
    width: 2.4rem;
    height: 2.4rem;
    display: grid;
    place-items: center;
    border: 0;
    border-radius: 0.55rem;
    background: none;
    color: ${brand.blue};
    cursor: pointer;

    svg {
      width: 1.2rem;
      height: 1.2rem;
    }
  }
`;

export const SheetButtons = styled.div`
  width: 100%;
  max-width: 26rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;

  > button:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }
`;

export const SheetButton = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-height: ${touchTarget};
  border-radius: 0.75rem;
  border: 1px solid
    ${({ $primary }) => ($primary ? brand.orange : surface.line)};
  background: ${({ $primary }) => ($primary ? brand.orange : brand.white)};
  color: ${({ $primary }) => ($primary ? brand.white : ink)};
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.9rem;
  cursor: pointer;
  touch-action: manipulation;

  svg {
    width: 1.1rem;
    height: 1.1rem;
  }

  &:active {
    transform: scale(0.97);
  }
`;
